import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import {
  productsAPI,
  uploadAPI,
  categoriesAPI,
  usersAPI,
  conditionsAPI,
  colorsAPI,
  locationsAPI,
  brandsAPI,
  closetAPI,
} from '../services/api';
import { influencerPublicName, personName } from '../utils/person';
import './ProductEdit.css';

const PRODUCT_SELECT_PROPS = {
  classNamePrefix: 'react-select',
  menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
  menuPosition: 'fixed',
};

const ProductSelect = (props) => <Select {...PRODUCT_SELECT_PROPS} {...props} />;

const ProductSection = ({ title, description, children, className = '' }) => (
  <section className={`product-edit-section ${className}`.trim()}>
    <div className="product-edit-section-head">
      <h3>{title}</h3>
      {description ? <p>{description}</p> : null}
    </div>
    <div className="product-edit-section-body">{children}</div>
  </section>
);

const ProductEdit = ({ product, onClose, onUpdate, influencerId, isClosetProduct }) => {
  const toDateTimeLocal = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    const yyyy = date.getFullYear();
    const mm = pad(date.getMonth() + 1);
    const dd = pad(date.getDate());
    const hh = pad(date.getHours());
    const min = pad(date.getMinutes());
    return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
  };

  const [formData, setFormData] = useState({
    title: '',
    title_ar: '',
    description: '',
    description_ar: '',
    price: '',
    stock: '1',
    status: 'pending',
    category_id: '',
    brand: '',
    condition_id: '',
    color_id: '',
    size: '',
    location_id: '',
    gender: '',
    seller_id: '',
    influencer_id: '',
    owner_type: 'marketplace',
    expires_at: '',
    is_verified: false,
    verification_image_url: '',
  });
  const [images, setImages] = useState([]);
  const [uploadingVerificationBadge, setUploadingVerificationBadge] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [conditions, setConditions] = useState([]);
  const [loadingConditions, setLoadingConditions] = useState(false);
  const [colors, setColors] = useState([]);
  const [loadingColors, setLoadingColors] = useState(false);
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [sellers, setSellers] = useState([]);
  const [loadingSellers, setLoadingSellers] = useState(false);
  const [closets, setClosets] = useState([]);
  const [loadingClosets, setLoadingClosets] = useState(false);
  const [brands, setBrands] = useState([]);
  const [loadingBrands, setLoadingBrands] = useState(false);
  const brandSearchTimerRef = useRef(null);

  // Load reference data on mount
  useEffect(() => {
    const loadReferenceData = async () => {
      // Load categories
      setLoadingCategories(true);
      try {
        const categoriesResponse = await categoriesAPI.getAll();
        const categoriesData = Array.isArray(categoriesResponse.data) 
          ? categoriesResponse.data 
          : (categoriesResponse.data?.data || []);
        setCategories(categoriesData);
      } catch (error) {
        console.error('Error loading categories:', error);
        toast.error('Error loading categories: ' + (error.response?.data?.message || error.message));
      } finally {
        setLoadingCategories(false);
      }

      // Load conditions
      setLoadingConditions(true);
      try {
        const conditionsResponse = await conditionsAPI.getAll();
        const conditionsData = conditionsResponse.data?.conditions || conditionsResponse.data || [];
        setConditions(conditionsData);
      } catch (error) {
        console.error('Error loading conditions:', error);
        toast.error('Error loading conditions: ' + (error.response?.data?.message || error.message));
      } finally {
        setLoadingConditions(false);
      }

      // Load colors
      setLoadingColors(true);
      try {
        const colorsResponse = await colorsAPI.getAll();
        const colorsData = colorsResponse.data?.colors || colorsResponse.data || [];
        setColors(colorsData);
      } catch (error) {
        console.error('Error loading colors:', error);
        toast.error('Error loading colors: ' + (error.response?.data?.message || error.message));
      } finally {
        setLoadingColors(false);
      }

      // Load locations
      setLoadingLocations(true);
      try {
        const locationsResponse = await locationsAPI.getAll();
        const locationsData = locationsResponse.data?.locations || locationsResponse.data || [];
        setLocations(locationsData);
      } catch (error) {
        console.error('Error loading locations:', error);
        toast.error('Error loading locations: ' + (error.response?.data?.message || error.message));
      } finally {
        setLoadingLocations(false);
      }

    };
    loadReferenceData();
  }, []);

  const fetchBrands = useCallback((search = '') => {
    setLoadingBrands(true);
    const params = { limit: 10 };
    if (search.trim()) params.search = search.trim();
    return brandsAPI
      .getAll(params)
      .then((response) => {
        const list = response.data?.brands || [];
        setBrands(list);
        return list;
      })
      .catch(() => {
        setBrands([]);
        return [];
      })
      .finally(() => {
        setLoadingBrands(false);
      });
  }, []);

  useEffect(() => {
    void fetchBrands('');
    return () => {
      if (brandSearchTimerRef.current) clearTimeout(brandSearchTimerRef.current);
    };
  }, [fetchBrands]);

  const handleBrandInputChange = (inputValue, { action }) => {
    if (action !== 'input-change') return;
    if (brandSearchTimerRef.current) clearTimeout(brandSearchTimerRef.current);
    brandSearchTimerRef.current = setTimeout(() => {
      void fetchBrands(inputValue);
    }, 300);
  };

  // Load sellers for marketplace products
  useEffect(() => {
    const loadSellers = async () => {
      setLoadingSellers(true);
      try {
        // Fetch all customer accounts (exclude closet influencers/admins) for seller selection.
        const perPage = 100;
        let page = 1;
        let hasMore = true;
        const allCustomers = [];

        while (hasMore && page <= 50) {
          const response = await usersAPI.getAll({ page, per_page: perPage });
          const pageItems = response.data?.data || [];
          const pagination = response.data?.pagination || {};
          allCustomers.push(
            ...pageItems.filter((user) => !user.is_admin && !user.is_influencer)
          );

          const currentPage = Number(pagination.page ?? page);
          const total = Number(pagination.total ?? 0);
          const perPageActual = Number(pagination.per_page ?? perPage);
          const lastPage = Number(
            pagination.last_page ??
              (total > 0 ? Math.ceil(total / perPageActual) : currentPage)
          );
          hasMore = currentPage < lastPage && pageItems.length > 0;
          page += 1;
        }

        setSellers(allCustomers);
      } catch (error) {
        console.error('Error loading sellers:', error);
        // Don't show error toast for sellers, as it's not critical
      } finally {
        setLoadingSellers(false);
      }
    };
    loadSellers();
  }, []);

  useEffect(() => {
    const loadClosets = async () => {
      setLoadingClosets(true);
      try {
        const perPage = 100;
        let page = 1;
        let hasMore = true;
        const allInfluencers = [];

        while (hasMore && page <= 50) {
          const response = await closetAPI.getInfluencers({ page, per_page: perPage });
          const pageItems = response.data?.data || [];
          const pagination = response.data?.pagination || {};
          allInfluencers.push(...pageItems);

          const currentPage = Number(pagination.page ?? page);
          const total = Number(pagination.total ?? 0);
          const perPageActual = Number(pagination.per_page ?? perPage);
          const lastPage = Number(
            pagination.last_page ??
              (total > 0 ? Math.ceil(total / perPageActual) : currentPage)
          );
          hasMore = currentPage < lastPage && pageItems.length > 0;
          page += 1;
        }

        setClosets(allInfluencers);
      } catch (error) {
        console.error('Error loading closets:', error);
      } finally {
        setLoadingClosets(false);
      }
    };
    loadClosets();
  }, []);

  useEffect(() => {
    if (product) {
      setFormData({
        title: product.title || '',
        title_ar: product.title_ar || '',
        description: product.description || '',
        description_ar: product.description_ar || '',
        price: product.price || '',
        stock:
          product.stock !== undefined && product.stock !== null
            ? String(product.stock)
            : '1',
        status: product.status || 'pending',
        category_id: product.category_id || product.category?.id || '',
        brand: product.brand || '',
        condition_id: product.condition_id || product.condition?.id || '',
        color_id: product.color_id ? String(product.color_id) : (product.color?.id ? String(product.color.id) : ''),
        size: product.size || '',
        location_id: product.location_id ? String(product.location_id) : (product.location?.id ? String(product.location.id) : ''),
        gender: product.gender || '',
        seller_id: product.seller?.id || product.seller_id || '',
        influencer_id: product.influencer?.id || product.influencer_id || '',
        owner_type: product.is_closet_product ? 'closet' : 'marketplace',
        expires_at: toDateTimeLocal(product.expires_at),
        is_verified: Boolean(product.is_verified),
        verification_image_url: product.verification_image_url || '',
      });

      if (product.images && product.images.length > 0) {
        const sortedImages = product.images.sort((a, b) => a.image_order - b.image_order);
        setImages(
          sortedImages.map((img) => ({
            url: img.image_url,
            isPrimary: img.is_primary,
            file: null, // For new file uploads
          }))
        );
      } else {
        setImages([{ url: '', isPrimary: true, file: null }]);
      }
    } else {
      // Create mode - initialize with empty form
      setFormData({
        title: '',
        title_ar: '',
        description: '',
        description_ar: '',
        price: '',
        stock: '1',
        status: 'pending',
        category_id: '',
        brand: '',
        condition_id: '',
        color_id: '',
        size: '',
        location_id: '',
        gender: '',
        seller_id: '',
        influencer_id: influencerId || '',
        owner_type: isClosetProduct ? 'closet' : 'marketplace',
        expires_at: '',
        is_verified: false,
        verification_image_url: '',
      });
      setImages([{ url: '', isPrimary: true, file: null }]);
    }
    
    // Handle special case: product object with is_closet_product flag (from Products.js)
    if (product && product.is_closet_product !== undefined && !product.id) {
      // This is a create mode with type specification
      setFormData({
        title: '',
        title_ar: '',
        description: '',
        description_ar: '',
        price: '',
        stock: '1',
        status: 'pending',
        category_id: '',
        brand: '',
        condition_id: '',
        color_id: '',
        size: '',
        location_id: '',
        gender: '',
        influencer_id: influencerId || '',
        owner_type: product.is_closet_product === true ? 'closet' : 'marketplace',
        expires_at: '',
        is_verified: false,
        verification_image_url: '',
      });
      setImages([{ url: '', isPrimary: true, file: null }]);
    }
  }, [product, influencerId, isClosetProduct]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleCategoryChange = (next) => {
    setFormData((prev) => {
      if (prev.category_id === next) return prev;
      return { ...prev, category_id: next };
    });
  };

  const handleBrandChange = (name) => {
    setFormData((prev) => ({ ...prev, brand: name }));
  };

  const categoryOptions = useMemo(
    () =>
      categories.map((cat) => ({
        value: String(cat.id || cat.uuid),
        label: cat.name,
      })),
    [categories]
  );

  const brandOptions = useMemo(() => {
    const opts = brands.map((b) => ({
      value: b.name,
      label: b.name,
    }));
    if (formData.brand && !opts.some((o) => o.value === formData.brand)) {
      opts.unshift({ value: formData.brand, label: formData.brand });
    }
    return opts;
  }, [brands, formData.brand]);

  const sellerOptions = useMemo(
    () =>
      sellers.map((seller) => ({
        value: String(seller.id),
        label: `${seller.first_name || ''} ${seller.last_name || ''} (${seller.email || 'No email'})`.trim(),
      })),
    [sellers]
  );
  const closetOptions = useMemo(
    () =>
      closets.map((influencer) => ({
        value: String(influencer.id),
        label: `${influencerPublicName(influencer)} (${influencer.email || 'No email'}) — ${personName(influencer)}`.trim(),
      })),
    [closets]
  );

  const conditionOptions = useMemo(
    () =>
      conditions.map((cond) => ({
        value: String(cond.id),
        label: cond.display_name || cond.name,
      })),
    [conditions]
  );

  const statusOptions = useMemo(
    () => [
      { value: 'pending', label: 'Pending' },
      { value: 'approved', label: 'Approved' },
      { value: 'rejected', label: 'Rejected' },
      { value: 'sold', label: 'Sold' },
    ],
    []
  );

  const colorOptions = useMemo(
    () =>
      colors.map((color) => ({
        value: String(color.id),
        label: color.name,
      })),
    [colors]
  );

  const locationOptions = useMemo(
    () =>
      locations.map((loc) => ({
        value: String(loc.id),
        label: `${loc.name}${loc.city ? ` (${loc.city})` : ''}`,
      })),
    [locations]
  );

  const genderOptions = useMemo(
    () => [
      { value: 'men', label: 'Men' },
      { value: 'women', label: 'Women' },
      { value: 'unisex', label: 'Unisex' },
      { value: 'kids', label: 'Kids' },
    ],
    []
  );
  const ownerTypeOptions = useMemo(
    () => [
      { value: 'marketplace', label: 'Marketplace (Customer)' },
      { value: 'closet', label: 'Closet (Influencer)' },
    ],
    []
  );

  const handleExtendExpiration = (daysToAdd = 7) => {
    const base = formData.expires_at ? new Date(formData.expires_at) : new Date();
    if (Number.isNaN(base.getTime())) return;
    const next = new Date(base.getTime() + daysToAdd * 24 * 60 * 60 * 1000);
    setFormData((prev) => ({
      ...prev,
      expires_at: toDateTimeLocal(next.toISOString()),
    }));
  };

  const handleImageChange = (index, value) => {
    const newImages = [...images];
    newImages[index].url = value;
    setImages(newImages);
  };

  const handleAddImage = () => {
    setImages([...images, { url: '', isPrimary: false, file: null }]);
  };

  const handleFileUpload = async (file, index) => {
    if (!file) return;

    setUploading(true);
    try {
      const response = await uploadAPI.uploadImage(file);
      const imageUrl = response.data.image_url;

      setImages((prev) => {
        const newImages = [...prev];
        if (index !== undefined && index !== null) {
          newImages[index] = {
            ...newImages[index],
            url: imageUrl,
            file: null,
          };
        } else {
          newImages.push({
            url: imageUrl,
            isPrimary: newImages.length === 0,
            file: null,
          });
        }
        return newImages;
      });
    } catch (error) {
      toast.error('Error uploading image: ' + (error.response?.data?.message || error.message));
    } finally {
      setUploading(false);
    }
  };

  const handleBulkUpload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;

    setUploading(true);
    let startIndex = 0;
    try {
      setImages((prev) => {
        startIndex = prev.length;
        const staged = files.map((file, idx) => ({
          url: URL.createObjectURL(file),
          isPrimary: prev.length === 0 && idx === 0,
          file,
        }));
        return [...prev, ...staged];
      });

      const { data } = await uploadAPI.uploadImages(files);
      const urlByBatchIndex = {};
      (data.images || []).forEach((row) => {
        urlByBatchIndex[row.index] = row.image_url;
      });

      setImages((prev) => {
        const next = [...prev];
        files.forEach((_, batchIdx) => {
          const targetIdx = startIndex + batchIdx;
          const url = urlByBatchIndex[batchIdx];
          if (url && next[targetIdx]) {
            next[targetIdx] = { ...next[targetIdx], url, file: null };
          }
        });
        return next;
      });

      if (data.failed > 0) {
        toast.warn(`${data.uploaded} uploaded, ${data.failed} failed`);
      } else {
        toast.success(`${data.uploaded} image(s) uploaded`);
      }
    } catch (error) {
      toast.error(
        'Bulk upload failed: ' + (error.response?.data?.message || error.response?.data?.error || error.message)
      );
    } finally {
      setUploading(false);
    }
  };

  const handleFileInputChange = (e, index) => {
    const file = e.target.files[0];
    if (file) {
      if (index !== undefined && index !== null) {
        // Update existing image
        const newImages = [...images];
        newImages[index] = {
          ...newImages[index],
          file: file,
          url: URL.createObjectURL(file), // Preview
        };
        setImages(newImages);
        handleFileUpload(file, index);
      } else {
        // Add new image
        handleFileUpload(file);
      }
    }
    e.target.value = ''; // Reset input
  };

  // Drag and drop handlers
  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    const newImages = [...images];
    const draggedImage = newImages[draggedIndex];
    
    // Remove dragged item
    newImages.splice(draggedIndex, 1);
    
    // Insert at new position
    newImages.splice(dropIndex, 0, draggedImage);
    
    // Update primary flag if needed
    if (draggedImage.isPrimary && dropIndex === 0) {
      // Already primary, no change needed
    } else if (dropIndex === 0 && !newImages[0].isPrimary) {
      // Make first image primary
      newImages[0].isPrimary = true;
    } else if (draggedIndex === 0 && dropIndex !== 0) {
      // Old primary was moved, make new first image primary
      newImages[0].isPrimary = true;
      draggedImage.isPrimary = false;
    }
    
    setImages(newImages);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleRemoveImage = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    if (newImages.length > 0 && newImages[0].isPrimary === false) {
      newImages[0].isPrimary = true;
    }
    setImages(newImages);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!formData.category_id) {
        toast.error('Please select a category.');
        setLoading(false);
        return;
      }
      if (loadingBrands) {
        toast.error('Please wait for brands to load.');
        setLoading(false);
        return;
      }
      if (brands.length === 0) {
        toast.error('Add at least one brand (Catalog → Brands) before saving.');
        setLoading(false);
        return;
      }
      if (!formData.brand) {
        toast.error('Please select a brand.');
        setLoading(false);
        return;
      }
      if (!formData.condition_id) {
        toast.error('Please select a condition.');
        setLoading(false);
        return;
      }
      if (!formData.location_id) {
        toast.error('Please select a location.');
        setLoading(false);
        return;
      }
      const stockNum = parseInt(formData.stock, 10);
      if (!Number.isFinite(stockNum) || stockNum < 0) {
        toast.error('Please enter a valid stock quantity (0 or more).');
        setLoading(false);
        return;
      }
      if (formData.owner_type === 'marketplace' && !formData.seller_id) {
        toast.error('Please select a seller.');
        setLoading(false);
        return;
      }
      if (formData.owner_type === 'closet' && !formData.influencer_id) {
        toast.error('Please select a closet account.');
        setLoading(false);
        return;
      }

      // Upload any pending file uploads in one batch request
      const updatedImages = [...images];
      const pendingItems = updatedImages
        .map((img, index) => ({ img, index }))
        .filter(
          ({ img }) =>
            img.file && (img.url.startsWith('blob:') || !img.url.startsWith('http'))
        );

      if (pendingItems.length > 0) {
        try {
          const { data } = await uploadAPI.uploadImages(pendingItems.map((p) => p.img.file));
          const urlByBatchIndex = {};
          (data.images || []).forEach((row) => {
            urlByBatchIndex[row.index] = row.image_url;
          });
          pendingItems.forEach((p, batchIdx) => {
            const url = urlByBatchIndex[batchIdx];
            if (url) {
              updatedImages[p.index] = { ...updatedImages[p.index], url, file: null };
            }
          });
          if (data.failed > 0) {
            toast.warn(`${data.failed} image(s) failed to upload on save`);
            if (data.uploaded === 0) {
              setLoading(false);
              return;
            }
          }
        } catch (error) {
          console.error('Error uploading images:', error);
          toast.error(
            `Error uploading images: ${error.response?.data?.message || error.response?.data?.error || error.message}`
          );
          setLoading(false);
          return;
        }
      }

      // Collect image URLs in current order (preserve drag-and-drop order)
      const imageUrls = updatedImages
        .filter((img) => img.url && img.url.trim() && !img.url.startsWith('blob:'))
        .map((img) => img.url.trim());

      // Prepare submit data with proper ID types and handle optional fields
      const submitData = {
        title: formData.title,
        title_ar: formData.title_ar || null,
        description: formData.description || '',
        description_ar: formData.description_ar || null,
        price: parseFloat(formData.price),
        stock: stockNum,
        currency: 'AED',
        status: formData.status,
        category_id: formData.category_id || null, // UUID - keep as string
        brand: formData.brand || '',
        brand_ar: null,
        condition_id: formData.condition_id ? parseInt(formData.condition_id, 10) : null,
        size: formData.size || '',
        images: imageUrls,
      };

      // Handle color_id (optional)
      if (formData.color_id && formData.color_id !== '') {
        submitData.color_id = parseInt(formData.color_id, 10);
      } else {
        submitData.color_id = null; // Explicitly set to null for optional field
      }

      // Handle location_id (required)
      if (formData.location_id && formData.location_id !== '') {
        submitData.location_id = parseInt(formData.location_id, 10);
      } else {
        submitData.location_id = null; // Will be validated by backend
      }

      // Handle gender (optional)
      if (formData.gender && formData.gender !== '') {
        submitData.gender = formData.gender;
      } else {
        submitData.gender = null; // Explicitly set to null for optional field
      }

      if (formData.expires_at && formData.expires_at !== '') {
        submitData.expires_at = formData.expires_at;
      } else {
        submitData.expires_at = null;
      }

      submitData.is_closet_product = formData.owner_type === 'closet';
      if (formData.owner_type === 'marketplace') {
        submitData.seller_id = formData.seller_id || null;
        submitData.influencer_id = null;
      } else {
        submitData.influencer_id = formData.influencer_id;
        submitData.seller_id = formData.influencer_id;
      }

      submitData.is_verified = Boolean(formData.is_verified);
      submitData.verification_image_url = formData.verification_image_url?.trim() || null;

      if (product && product.id) {
        // Edit mode
        await productsAPI.update(product.id, submitData);
        toast.success('Product updated successfully!');
      } else {
        // Create mode
        await productsAPI.create(submitData);
        toast.success('Product created successfully!');
      }
      onUpdate();
    } catch (error) {
      const action = (product && product.id) ? 'updating' : 'creating';
      toast.error(`Error ${action} product: ` + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Product can be null for create mode

  return (
    <div className="product-edit">
      <form onSubmit={handleSubmit} className="product-edit-form">
        <div className="product-edit-layout">
          <div className="product-edit-main">
            <ProductSection
              title="Photos"
              description="Drag to reorder. The first photo is the cover image shown in the app."
            >
              <div className="product-images-toolbar">
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  disabled={uploading}
                  onChange={(e) => {
                    handleBulkUpload(e.target.files);
                    e.target.value = '';
                  }}
                  className="file-input"
                  id="bulk-upload"
                />
                <label
                  htmlFor="bulk-upload"
                  className="btn btn-success"
                  style={{ opacity: uploading ? 0.6 : 1, cursor: uploading ? 'wait' : 'pointer' }}
                >
                  {uploading ? 'Uploading…' : 'Upload photos'}
                </label>
                <button type="button" className="btn btn-secondary" onClick={handleAddImage}>
                  Add empty slot
                </button>
              </div>

              {images.length === 0 ? (
                <div className="product-images-empty">
                  No photos yet. Upload multiple images at once, or add a slot and paste a URL.
                </div>
              ) : (
                <div className="product-images-grid">
                  {images.map((img, index) => (
                    <div
                      key={index}
                      className={`image-item-edit ${index === 0 ? 'is-cover' : ''} ${draggedIndex === index ? 'dragging' : ''} ${dragOverIndex === index ? 'drag-over' : ''}`}
                      draggable
                      onDragStart={(e) => handleDragStart(e, index)}
                      onDragOver={(e) => handleDragOver(e, index)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, index)}
                      onDragEnd={handleDragEnd}
                    >
                      <div className="image-card-preview">
                        {index === 0 ? <span className="image-cover-badge">Cover</span> : null}
                        <span className="drag-handle" title="Drag to reorder">⋮⋮</span>
                        {img.url || img.file ? (
                          <>
                            <img
                              src={img.url || (img.file ? URL.createObjectURL(img.file) : '')}
                              alt={`Product ${index + 1}`}
                              onError={(e) => {
                                e.target.style.display = 'none';
                              }}
                            />
                            {img.file ? (
                              <div className="upload-indicator">
                                {uploading ? 'Uploading…' : 'Pending save'}
                              </div>
                            ) : null}
                          </>
                        ) : (
                          <span className="image-card-placeholder">No image</span>
                        )}
                      </div>
                      <div className="image-card-body">
                        <div className="image-card-actions">
                          <input
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleFileInputChange(e, index)}
                            className="file-input"
                            id={`file-input-${index}`}
                          />
                          <label htmlFor={`file-input-${index}`} className="btn btn-upload">
                            Replace
                          </label>
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={() => handleRemoveImage(index)}
                          >
                            Remove
                          </button>
                        </div>
                        <input
                          type="url"
                          value={img.url && !img.url.startsWith('blob:') ? img.url : ''}
                          onChange={(e) => handleImageChange(index, e.target.value)}
                          placeholder="Or paste image URL"
                          className="image-url-input"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ProductSection>

            <ProductSection title="Basic info" description="Title, price, and descriptions shown on the product page.">
              <div className="form-row">
                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Title (Arabic)</label>
                  <input
                    type="text"
                    name="title_ar"
                    value={formData.title_ar}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Price (AED) *</label>
                  <input
                    type="number"
                    name="price"
                    step="0.01"
                    min="0"
                    value={formData.price}
                    onChange={handleInputChange}
                    required
                    style={{ maxWidth: 220 }}
                  />
                </div>
                <div className="form-group">
                  <label>Stock *</label>
                  <input
                    type="number"
                    name="stock"
                    min="0"
                    step="1"
                    value={formData.stock}
                    onChange={handleInputChange}
                    required
                    style={{ maxWidth: 220 }}
                  />
                  <p className="product-edit-brand-hint" style={{ marginTop: 6 }}>
                    Units available. Use 0 when sold out (usually 1).
                  </p>
                </div>
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  rows="4"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Condition notes, measurements, styling details…"
                />
              </div>
              <div className="form-group">
                <label>Description (Arabic)</label>
                <textarea
                  name="description_ar"
                  rows="4"
                  value={formData.description_ar}
                  onChange={handleInputChange}
                />
              </div>
            </ProductSection>

            <ProductSection title="Details" description="Optional attributes for filters and listing cards.">
              <div className="form-row cols-3">
                <div className="form-group">
                  <label>Color</label>
                  {loadingColors ? (
                    <ProductSelect isDisabled options={[]} placeholder="Loading…" />
                  ) : (
                    <ProductSelect
                      inputId="color_id"
                      name="color_id"
                      options={colorOptions}
                      value={colorOptions.find((opt) => opt.value === String(formData.color_id)) || null}
                      onChange={(option) =>
                        setFormData((prev) => ({
                          ...prev,
                          color_id: option ? option.value : '',
                        }))
                      }
                      placeholder="Optional"
                      isClearable
                      isSearchable
                    />
                  )}
                </div>
                <div className="form-group">
                  <label>Size</label>
                  <input type="text" name="size" value={formData.size} onChange={handleInputChange} placeholder="e.g. M, 38" />
                </div>
                <div className="form-group">
                  <label>Gender</label>
                  <ProductSelect
                    inputId="gender"
                    name="gender"
                    options={genderOptions}
                    value={genderOptions.find((opt) => opt.value === formData.gender) || null}
                    onChange={(option) =>
                      setFormData((prev) => ({
                        ...prev,
                        gender: option ? option.value : '',
                      }))
                    }
                    placeholder="Optional"
                    isClearable
                    isSearchable={false}
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Location *</label>
                {loadingLocations ? (
                  <ProductSelect isDisabled options={[]} placeholder="Loading…" />
                ) : (
                  <ProductSelect
                    inputId="location_id"
                    name="location_id"
                    options={locationOptions}
                    value={locationOptions.find((opt) => opt.value === String(formData.location_id)) || null}
                    onChange={(option) =>
                      setFormData((prev) => ({
                        ...prev,
                        location_id: option ? option.value : '',
                      }))
                    }
                    placeholder="Select a location"
                    isClearable
                    isSearchable
                  />
                )}
              </div>
            </ProductSection>
          </div>

          <aside className="product-edit-sidebar">
            <ProductSection title="Catalog" description="Required fields for publishing.">
              <div className="form-group">
                <label>Category *</label>
                {loadingCategories ? (
                  <ProductSelect isDisabled options={[]} placeholder="Loading…" />
                ) : (
                  <ProductSelect
                    inputId="category_id"
                    name="category_id"
                    options={categoryOptions}
                    value={categoryOptions.find((opt) => opt.value === String(formData.category_id)) || null}
                    onChange={(option) => handleCategoryChange(option ? option.value : '')}
                    placeholder="Select category"
                    isClearable
                    isSearchable
                  />
                )}
              </div>
              <div className="form-group">
                <label>Brand *</label>
                {loadingBrands ? (
                  <ProductSelect isDisabled options={[]} placeholder="Loading…" />
                ) : brands.length === 0 ? (
                  <p className="product-edit-brand-hint">
                    No brands yet — add some under <strong>Catalog → Brands</strong>.
                  </p>
                ) : (
                  <ProductSelect
                    inputId="brand"
                    name="brand"
                    options={brandOptions}
                    value={brandOptions.find((opt) => opt.value === formData.brand) || null}
                    onChange={(option) => handleBrandChange(option ? option.value : '')}
                    onInputChange={handleBrandInputChange}
                    filterOption={() => true}
                    placeholder="Search brands"
                    isClearable
                    isSearchable
                    noOptionsMessage={() => (loadingBrands ? 'Loading…' : 'Type to search')}
                  />
                )}
              </div>
              <div className="form-group">
                <label>Condition *</label>
                {loadingConditions ? (
                  <ProductSelect isDisabled options={[]} placeholder="Loading…" />
                ) : (
                  <ProductSelect
                    inputId="condition_id"
                    name="condition_id"
                    options={conditionOptions}
                    value={conditionOptions.find((opt) => opt.value === String(formData.condition_id)) || null}
                    onChange={(option) =>
                      setFormData((prev) => ({
                        ...prev,
                        condition_id: option ? option.value : '',
                      }))
                    }
                    placeholder="Select condition"
                    isClearable
                    isSearchable
                  />
                )}
              </div>
            </ProductSection>

            <ProductSection title="Listing" description="Who owns this item and how it appears in the app.">
              <div className="form-group">
                <label>Belongs to *</label>
                <ProductSelect
                  inputId="owner_type"
                  name="owner_type"
                  options={ownerTypeOptions}
                  value={ownerTypeOptions.find((opt) => opt.value === formData.owner_type) || null}
                  onChange={(option) =>
                    setFormData((prev) => ({
                      ...prev,
                      owner_type: option ? option.value : 'marketplace',
                      seller_id: option?.value === 'closet' ? '' : prev.seller_id,
                      influencer_id: option?.value === 'marketplace' ? '' : prev.influencer_id,
                    }))
                  }
                  isSearchable={false}
                />
              </div>

              {formData.owner_type === 'marketplace' ? (
                <div className="form-group">
                  <label>Seller *</label>
                  {loadingSellers ? (
                    <ProductSelect isDisabled options={[]} placeholder="Loading…" />
                  ) : (
                    <ProductSelect
                      inputId="seller_id"
                      name="seller_id"
                      options={sellerOptions}
                      value={sellerOptions.find((opt) => opt.value === String(formData.seller_id)) || null}
                      onChange={(option) =>
                        setFormData((prev) => ({
                          ...prev,
                          seller_id: option ? option.value : '',
                        }))
                      }
                      placeholder="Select customer"
                      isClearable
                      isSearchable
                    />
                  )}
                </div>
              ) : (
                <div className="form-group">
                  <label>Closet account *</label>
                  {loadingClosets ? (
                    <ProductSelect isDisabled options={[]} placeholder="Loading…" />
                  ) : (
                    <ProductSelect
                      inputId="influencer_id"
                      name="influencer_id"
                      options={closetOptions}
                      value={closetOptions.find((opt) => opt.value === String(formData.influencer_id)) || null}
                      onChange={(option) =>
                        setFormData((prev) => ({
                          ...prev,
                          influencer_id: option ? option.value : '',
                        }))
                      }
                      placeholder="Select closet"
                      isClearable
                      isSearchable
                    />
                  )}
                </div>
              )}

              <div className="form-group">
                <label>Status</label>
                <ProductSelect
                  inputId="status"
                  name="status"
                  options={statusOptions}
                  value={statusOptions.find((opt) => opt.value === formData.status) || null}
                  onChange={(option) =>
                    setFormData((prev) => ({
                      ...prev,
                      status: option ? option.value : 'pending',
                    }))
                  }
                  isSearchable={false}
                />
              </div>

              <label className="product-edit-inline-check">
                <input
                  type="checkbox"
                  checked={formData.is_verified}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, is_verified: e.target.checked }))
                  }
                />
                <span>Verified badge in app</span>
              </label>

              {formData.is_verified ? (
                <div className="product-edit-verified-extra">
                  <label>Custom badge image (optional)</label>
                  <input
                    type="file"
                    accept="image/*"
                    className="file-input"
                    id="verification-badge-upload"
                    disabled={uploadingVerificationBadge}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setUploadingVerificationBadge(true);
                      try {
                        const response = await uploadAPI.uploadImage(file);
                        setFormData((prev) => ({
                          ...prev,
                          verification_image_url: response.data.image_url,
                        }));
                        toast.success('Badge image uploaded');
                      } catch (error) {
                        toast.error(
                          error.response?.data?.message || error.message || 'Upload failed'
                        );
                      } finally {
                        setUploadingVerificationBadge(false);
                        e.target.value = '';
                      }
                    }}
                  />
                  <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <label htmlFor="verification-badge-upload" className="btn btn-secondary btn-sm">
                      {uploadingVerificationBadge ? 'Uploading…' : 'Upload badge'}
                    </label>
                    {formData.verification_image_url ? (
                      <>
                        <img
                          src={formData.verification_image_url}
                          alt="Badge"
                          style={{ maxHeight: 36, maxWidth: 120, borderRadius: 4 }}
                        />
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() =>
                            setFormData((prev) => ({ ...prev, verification_image_url: '' }))
                          }
                        >
                          Remove
                        </button>
                      </>
                    ) : null}
                  </div>
                </div>
              ) : null}

              <div className="form-group" style={{ marginTop: 14 }}>
                <label>Expires at</label>
                <input
                  type="datetime-local"
                  name="expires_at"
                  value={formData.expires_at}
                  onChange={handleInputChange}
                />
                <div className="product-edit-expiry-actions">
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => handleExtendExpiration(7)}>
                    +7 days
                  </button>
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => handleExtendExpiration(30)}>
                    +30 days
                  </button>
                </div>
              </div>
            </ProductSection>
          </aside>
        </div>

        <div className="product-edit-footer">
          <button type="submit" className="btn btn-primary" disabled={loading || uploading}>
            {loading
              ? product
                ? 'Saving…'
                : 'Creating…'
              : product
                ? 'Save product'
                : 'Create product'}
          </button>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProductEdit;

